/**
 * Decode the HTML entities that DIRECTV CMS and Epoch copy actually carries.
 *
 * The customer-facing symptom is "Max&#8482;" on screen instead of "Max™"
 * (WAT-11842 item 28).
 *
 * Observed live in the agent tool's own page payload (2026-09-03), which is why
 * both forms are handled rather than just the numeric one:
 *   numeric — "Espa&#241;ol", "DIRECTV&#174; Genie"
 *   named   — "DIRECTV&reg; Deportes", "EN ESPA&Ntilde;OL&trade;", "&Oacute;PTIMO M&Aacute;S"
 *
 * 🔴 WHERE THIS IS LOAD-BEARING, AND WHERE IT IS NOT. The tool renders names
 * through two different paths and they do NOT behave the same:
 *
 *   - MUI `<Text>{name}</Text>` (SelectablePackCard, SummaryColumns) — React
 *     escapes children, so an entity survives to the screen. The decode IS the
 *     fix here.
 *   - DDS `<DtvText content={name} />` (IncludedContentSection, AddChannelModal)
 *     — `content` goes through `validContent` → `html-react-parser`
 *     (dtv-text.tsx:70 calls `validContent` from @directv-digital/dds.utils;
 *     the `parse()` itself is at dds.utils/parseHtml.ts:203), which ALREADY decodes
 *     entities. Measured: `parse('Max&#8482;')` → "Max™". The decode is a no-op
 *     here, kept only so one producer feeds both paths with one string.
 *   - String COMPARISONS (the `contentNames` and `addonAndSvodNames` dedupe
 *     filters) — plain JS, no parser. The decode is what makes two encodings of
 *     one name compare equal, which is the duplicate-row half of item 28.
 *
 * 🔴 SO THIS DECODER MUST NEVER PRODUCE MARKUP. Because one of those render
 * paths hands the result to an HTML parser, emitting `<`, `>`, `&`, `"` or `'`
 * would convert text an author escaped ON PURPOSE into live DOM: measured,
 * `parse('&lt;b&gt;Max&lt;/b&gt;')` renders the literal text "<b>Max</b>", but
 * decode it first and `parse('<b>Max</b>')` returns a real <b> ELEMENT — and
 * `&lt;img src=x onerror=1&gt;` becomes a real <img>. The five XML entities are
 * therefore NOT in the map, and `safeChar` below rejects the same characters
 * arriving by their numeric codes, so there is no second way in. Escaped markup
 * keeps its raw text and stays inert on both paths.
 *
 * ponytail: an explicit map of the entities this content set uses, not a full
 * HTML5 entity table and not a new dependency — `entities` is present in
 * node_modules but only transitively, so importing it would break on a hoist
 * change. Add a row when a new entity turns up; if this ever outgrows a
 * screenful, take `entities` as a direct dependency instead. Anything not in the
 * map passes through unchanged rather than being dropped, so an unknown entity
 * degrades to today's behaviour instead of losing text.
 */

/**
 * The named entities present in DIRECTV CMS/Epoch copy — the typographic and
 * accented ones only.
 *
 * The XML five (`amp` `lt` `gt` `quot` `apos`) are deliberately ABSENT; see the
 * markup note above. Their omission costs nothing on screen, because the one
 * render path where they would matter is the DtvText/parser path, which decodes
 * them itself.
 *
 * A `Map`, deliberately, NOT an object literal. The lookup key comes straight
 * out of untrusted feed text, and a plain object inherits `Object.prototype`, so
 * `&toString;` / `&constructor;` / `&valueOf;` would each resolve to an
 * inherited FUNCTION — non-nullish, so a `?? raw` fallback never fires and the
 * function's source text lands in the rendered copy. A `Map` has no such keys.
 * (Same hazard `cellsForKey` guards in lib/service/dtvcomms/quote-email-html.ts.)
 */
const NAMED_ENTITIES = new Map<string, string>(Object.entries({
  nbsp: String.fromCharCode(0xa0),
  copy: '©',
  reg: '®',
  trade: '™',
  aacute: 'á',
  Aacute: 'Á',
  eacute: 'é',
  Eacute: 'É',
  iacute: 'í',
  Iacute: 'Í',
  ntilde: 'ñ',
  Ntilde: 'Ñ',
  oacute: 'ó',
  Oacute: 'Ó',
  uacute: 'ú',
  Uacute: 'Ú',
  uuml: 'ü',
  Uuml: 'Ü'
}));

/** `<`, `>`, `&`, `"`, `'` — the characters an HTML parser acts on. */
const MARKUP_CHARS = new Set(['<', '>', '&', '"', "'"]);

/**
 * ONE gate every decoded character passes through, numeric and named alike, so
 * there is a single place that decides what may reach a label. Rejecting here
 * rather than filtering the map is what closes the numeric back door: the map
 * has no `lt` row, but `&#60;` would otherwise still yield `<`.
 *
 * Rejected, each keeping its raw entity text:
 *  - markup characters (see the file header — an author-escaped `&lt;` must stay
 *    inert on the DtvText/parser path);
 *  - C0 controls including NUL, which render as nothing and would sit invisibly
 *    inside a `Set`/`Map` key and the emailed quote (`&#0;`);
 *  - lone surrogates, which are legal for `String.fromCodePoint` but make the
 *    string ill-formed — `encodeURIComponent` THROWS `URIError: URI malformed`
 *    on one, inside a render path whose contract is never-throw (`&#xD800;`);
 *  - anything `String.fromCodePoint` itself rejects.
 */
const safeChar = (codePoint: number, raw: string): string => {
  if (!Number.isInteger(codePoint) || codePoint < 0x20 || codePoint > 0x10ffff) {
    return raw;
  }
  if (codePoint >= 0xd800 && codePoint <= 0xdfff) {
    return raw;
  }
  const char = String.fromCodePoint(codePoint);
  return MARKUP_CHARS.has(char) ? raw : char;
};

/**
 * Hex-numeric, decimal-numeric and named entities in ONE alternation, matched in
 * a SINGLE pass on purpose.
 *
 * Chained per-form passes double-decode: the numeric pass turns `&#38;lt;` into
 * `&lt;`, which a later named pass then turns into `<` — text the author escaped
 * twice to display literally comes out as markup. One pass consumes each match
 * and never revisits its own output. (Belt and braces now: `&#38;` is also
 * rejected by `safeChar`, so `&#38;lt;` survives intact for two reasons.)
 */
const ENTITY = /&(?:#[xX]([0-9a-fA-F]+)|#(\d+)|([a-zA-Z]+));/g;

/**
 * Decode hex-numeric, decimal-numeric and named HTML entities. Non-string input
 * (including the `{ en }` shape some feeds use) yields an empty string, matching
 * the `|| ''` fallback every call site already had.
 */
export const decodeHtmlEntities = (value?: string): string => (
  typeof value === 'string' ?
    value.replace(ENTITY, (raw, hex, digits, name) => {
      if (hex != null) {
        return safeChar(parseInt(hex, 16), raw);
      }
      if (digits != null) {
        return safeChar(Number(digits), raw);
      }
      return NAMED_ENTITIES.get(name) ?? raw;
    }) :
    ''
);
